import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { Chips, Loading, PageHead, fmtWhen, useAdmin } from "./ui";

export const TAB_LABELS = [["dashboard", "Dashboard"], ["my-calendar", "My calendar (own bookings only)"], ["calendar", "Calendar (all bookings)"], ["team", "Team & schedules"], ["bookings", "Bookings"], ["payments", "Service sales"], ["customers", "Customers"],
  ["messages", "Messages"], ["subscribers", "Subscribers"], ["popups", "Pop-ups"], ["content", "Site content"], ["emails", "Emails"], ["work", "Our work"], ["reviews", "Reviews"],
  ["music", "Music"], ["shop", "Shop items"], ["shop-sales", "Shop sales"], ["services", "Services & prices"], ["settings", "Settings"]];
const ALL = TAB_LABELS.map(([k]) => k);
const PRESETS = [
  ["Front desk", ["dashboard", "calendar", "bookings", "payments", "customers", "messages"]],
  ["Shop manager", ["shop", "shop-sales", "customers", "messages"]],
  ["Marketing", ["popups", "content", "emails", "work", "reviews", "music", "subscribers"]],
  ["Everything", ALL],
];

const TabPicker = ({ tabs, setTabs }) => (
  <div className="st-tabs" data-testid="staff-tab-picker">
    <div className="st-presets">
      {PRESETS.map(([l, t]) => <button type="button" key={l} className="cu-suggest" onClick={() => setTabs(t)} data-testid={`staff-preset-${l.toLowerCase().replace(/ /g, "-")}`}>{l}</button>)}
      <button type="button" className="cu-suggest" onClick={() => setTabs([])} data-testid="staff-preset-none">Clear</button>
    </div>
    <div className="st-grid">
      {TAB_LABELS.map(([k, l]) => (
        <label key={k} className="st-check">
          <input type="checkbox" checked={tabs.includes(k)} onChange={(e) => setTabs(e.target.checked ? [...tabs, k] : tabs.filter((x) => x !== k))} data-testid={`staff-tab-${k}`} /> {l}
        </label>
      ))}
    </div>
  </div>
);

const Editor = ({ user, onDone }) => {
  const isNew = !user.id;
  const [f, setF] = useState({ name: user.name || "", email: user.email || "", password: "", role: user.role || "staff", tabs: user.tabs || [] });
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); qc.invalidateQueries({ queryKey: ["admin", "staff"] }); toast.success(msg); onDone(); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const save = (e) => {
    e.preventDefault();
    if (f.role === "staff" && !f.tabs.length) { toast.error("Tick at least one tab this person can open"); return; }
    if (isNew) run(() => api.post("/admin/staff", f), `Login created for ${f.email}`);
    else run(async () => {
      await api.patch(`/admin/staff/${user.id}`, { name: f.name, email: f.email.trim().toLowerCase(), role: f.role, tabs: f.tabs });
      if (f.password) await api.post(`/admin/staff/${user.id}/password`, { password: f.password });
    }, f.password ? "Saved and password reset" : "Login updated");
  };
  return (
    <form className="st-form" onSubmit={save} data-testid="staff-form">
      <DialogTitle className="ad-msg-title">{isNew ? "New staff login" : `Edit ${user.name}`}</DialogTitle>
      <label className="mu-field">Name<input required maxLength={60} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="staff-name-input" /></label>
      <label className="mu-field">Email (used to log in)<input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="staff-email-input" /></label>
      <label className="mu-field">{isNew ? "Password" : "New password (leave blank to keep)"}
        <input type="text" required={isNew} minLength={8} maxLength={128} autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="At least 8 characters" data-testid="staff-password-input" />
      </label>
      <Chips options={[["staff", "Staff (limited tabs)"], ["owner", "Owner (everything + staff logins)"]]} value={f.role} onChange={(role) => setF({ ...f, role })} testPrefix="staff-role" />
      {f.role === "staff" ? <TabPicker tabs={f.tabs} setTabs={(tabs) => setF({ ...f, tabs })} /> : <p className="muted-sm">Owners can open every tab and manage staff logins.</p>}
      <button className="btn gold" type="submit" disabled={busy} data-testid="staff-save-button">{busy ? "Saving…" : isNew ? "Create login" : "Save changes"}</button>
    </form>
  );
};

const Card = ({ u, me, onEdit }) => {
  const qc = useQueryClient();
  const act = async (fn, msg) => {
    try { await fn(); qc.invalidateQueries({ queryKey: ["admin", "staff"] }); toast.success(msg); } catch (err) { toast.error(formatApiError(err)); }
  };
  const self = u.id === me.id;
  return (
    <div className={`ad-card st-card ${u.active ? "" : "off"}`} data-testid={`staff-card-${u.email}`}>
      <div className="st-top">
        <div><strong>{u.name}{self && " (you)"}</strong><small>{u.email}</small></div>
        <span className={`pill ${u.is_owner ? "st-owner" : ""}`} data-testid="staff-card-role">{u.is_owner ? "Owner" : "Staff"}</span>
      </div>
      <small className="muted-sm" data-testid="staff-card-tabs">{u.is_owner ? "All tabs" : TAB_LABELS.filter(([k]) => u.tabs.includes(k)).map(([, l]) => l).join(" · ") || "No tabs"}</small>
      <small className="muted-sm" data-testid="staff-card-last-login">Last login: {u.last_login_at ? fmtWhen(u.last_login_at) : "never"}</small>
      <div className="req-actions">
        <button className="btn light" onClick={onEdit} data-testid="staff-edit-button"><KeyRound size={14} /> Edit / reset password</button>
        {!self && <label className="st-check" data-testid="staff-active-toggle">
          <input type="checkbox" checked={u.active} onChange={(e) => act(() => api.patch(`/admin/staff/${u.id}`, { active: e.target.checked }), e.target.checked ? "Login turned on" : "Login turned off")} data-testid="staff-active-checkbox" /> Login on
        </label>}
        {!self && <button className="btn light" onClick={() => window.confirm(`Delete the login for ${u.email}?`) && act(() => api.delete(`/admin/staff/${u.id}`), "Login deleted")} data-testid="staff-delete-button"><Trash2 size={14} /></button>}
      </div>
    </div>
  );
};

export default function Staff({ me }) {
  const { data } = useAdmin("staff", "/admin/staff");
  const [edit, setEdit] = useState(null);
  return (
    <div data-testid="admin-staff">
      <PageHead eyebrow="Team access" title="Staff logins">
        <button className="btn gold" onClick={() => setEdit({})} data-testid="staff-new-button"><Plus size={15} /> New login</button>
      </PageHead>
      {!data ? <Loading /> : <div className="st-list">{data.map((u) => <Card key={u.id} u={u} me={me} onEdit={() => setEdit(u)} />)}</div>}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="ad-msg-dialog" aria-describedby={undefined} data-testid="staff-dialog">{edit && <Editor key={edit.id || "new"} user={edit} onDone={() => setEdit(null)} />}</DialogContent>
      </Dialog>
    </div>
  );
}
